import { useEffect, useState } from 'react'
import { api } from '../utils/api'

interface Subscriber { id: string; email: string; name?: string | null; createdAt: string }
interface Campaign { id: string; subject: string; recipientCount: number; status: string; sentAt: string }

interface Props {
  siteId: string | null
  onClose: () => void
}

type Tab = 'compose' | 'subscribers' | 'history'

function fmtDate(d: string) {
  return new Date(d).toLocaleString('ru-RU', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' })
}

export function NewsletterPanel({ siteId, onClose }: Props) {
  const [tab, setTab] = useState<Tab>('compose')
  const [subscribers, setSubscribers] = useState<Subscriber[]>([])
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [loading, setLoading] = useState(false)

  // Compose state
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState<{ recipientCount: number } | null>(null)
  const [preview, setPreview] = useState(false)

  const loadData = () => {
    if (!siteId) return
    setLoading(true)
    Promise.all([
      api.get<Subscriber[]>(`/sites/${siteId}/newsletter/subscribers`),
      api.get<Campaign[]>(`/sites/${siteId}/newsletter/campaigns`),
    ]).then(([subs, camps]) => {
      setSubscribers(subs)
      setCampaigns(camps)
    }).catch(() => null).finally(() => setLoading(false))
  }

  useEffect(() => { loadData() }, [siteId])

  const handleSend = async () => {
    if (!siteId || !subject.trim() || !body.trim()) return
    if (!confirm(`Отправить рассылку "${subject}" на ${subscribers.length} адресов?`)) return
    setSending(true)
    try {
      const htmlContent = body.replace(/\n/g, '<br>')
      const result = await api.post<{ recipientCount: number }>(`/sites/${siteId}/newsletter/send`, { subject, htmlContent })
      setSent(result)
      setSubject('')
      setBody('')
      loadData()
    } catch {
      alert('Ошибка отправки. Проверьте настройки SMTP в backend.')
    } finally {
      setSending(false)
    }
  }

  const handleDeleteSub = async (id: string) => {
    if (!siteId) return
    await api.delete(`/sites/${siteId}/newsletter/subscribers/${id}`).catch(() => null)
    setSubscribers(s => s.filter(x => x.id !== id))
  }

  const handleExport = () => {
    if (!siteId) return
    const link = document.createElement('a')
    link.href = `/api/sites/${siteId}/newsletter/export`
    link.download = 'subscribers.csv'
    link.click()
  }

  const activeSubs = subscribers.filter(s => !('unsubscribedAt' in s))

  const tabs: [Tab, string][] = [
    ['compose', '✉️ Рассылка'],
    ['subscribers', `👥 Подписчики (${activeSubs.length})`],
    ['history', '📋 История'],
  ]

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9000, display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{ width: '600px', maxWidth: '95vw', height: '100vh', background: 'var(--bg-1)', borderLeft: '1px solid var(--border)', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>

        {/* Header */}
        <div style={{ padding: '20px 24px 0', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, background: 'var(--bg-1)', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text-primary)' }}>Email-рассылка</div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '20px', color: 'var(--text-muted)', cursor: 'pointer', lineHeight: 1 }}>×</button>
          </div>
          <div style={{ display: 'flex', gap: '0' }}>
            {tabs.map(([t, label]) => (
              <button key={t} onClick={() => setTab(t)}
                style={{ padding: '8px 14px', border: 'none', background: 'none', fontSize: '12px', fontWeight: 600, color: tab === t ? 'var(--accent)' : 'var(--text-muted)', borderBottom: tab === t ? '2px solid var(--accent)' : '2px solid transparent', cursor: 'pointer', marginBottom: '-1px', whiteSpace: 'nowrap' }}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {!siteId ? (
          <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '60px' }}>Сохраните сайт для работы с рассылкой</div>
        ) : loading ? (
          <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '60px' }}>Загрузка...</div>
        ) : tab === 'compose' ? (
          <div style={{ padding: '24px', flex: 1 }}>
            {sent ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
                <div style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '8px' }}>Рассылка отправлена!</div>
                <div style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '24px' }}>Письмо получили {sent.recipientCount} подписчиков</div>
                <button onClick={() => setSent(null)}
                  style={{ padding: '10px 24px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}>
                  Новое письмо
                </button>
              </div>
            ) : (
              <>
                <div style={{ background: 'var(--bg-2)', borderRadius: '10px', padding: '14px 16px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '20px' }}>👥</span>
                  <div>
                    <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-primary)' }}>{activeSubs.length} активных подписчиков</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Письмо будет отправлено на все активные адреса</div>
                  </div>
                </div>

                {activeSubs.length === 0 && (
                  <div style={{ background: '#fef3c7', borderRadius: '10px', padding: '14px 16px', marginBottom: '20px', fontSize: '13px', color: '#92400e' }}>
                    Нет подписчиков. Добавьте блок «Подписка на новости» на страницу сайта, чтобы собирать email-адреса.
                  </div>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.6px', display: 'block', marginBottom: '6px' }}>Тема письма</label>
                    <input className="prop-input" value={subject} onChange={e => setSubject(e.target.value)}
                      placeholder="Например: Новинки этой недели 🔥" style={{ width: '100%', boxSizing: 'border-box' }} />
                  </div>

                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.6px' }}>Текст письма</label>
                      <button onClick={() => setPreview(p => !p)}
                        style={{ fontSize: '11px', padding: '3px 8px', background: preview ? 'var(--accent)' : 'var(--bg-2)', color: preview ? '#fff' : 'var(--text-secondary)', border: '1px solid var(--border)', borderRadius: '5px', cursor: 'pointer' }}>
                        {preview ? 'Редактор' : 'Превью'}
                      </button>
                    </div>
                    {preview ? (
                      <div style={{ border: '1px solid var(--border)', borderRadius: '8px', padding: '16px', background: '#fff', color: '#111', fontSize: '14px', lineHeight: 1.6, minHeight: '200px' }}
                        dangerouslySetInnerHTML={{ __html: body.replace(/\n/g, '<br>') }} />
                    ) : (
                      <textarea value={body} onChange={e => setBody(e.target.value)}
                        placeholder="Напишите текст письма. Переносы строк сохранятся."
                        style={{ width: '100%', boxSizing: 'border-box', minHeight: '220px', padding: '10px 12px', fontSize: '13px', border: '1px solid var(--border)', borderRadius: '8px', background: 'var(--bg-2)', color: 'var(--text-primary)', resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6 }} />
                    )}
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
                      Текст конвертируется в HTML. Вы также можете вставить HTML-разметку.
                    </div>
                  </div>

                  <button onClick={handleSend} disabled={sending || !subject.trim() || !body.trim() || activeSubs.length === 0}
                    style={{ padding: '12px 24px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 700, cursor: 'pointer', opacity: sending || !subject.trim() || !body.trim() || activeSubs.length === 0 ? 0.6 : 1, marginTop: '8px' }}>
                    {sending ? '⏳ Отправка...' : `📨 Отправить ${activeSubs.length} подписчикам`}
                  </button>
                </div>
              </>
            )}
          </div>
        ) : tab === 'subscribers' ? (
          <div style={{ padding: '20px 24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{activeSubs.length} активных подписчиков</div>
              <button onClick={handleExport}
                style={{ padding: '6px 14px', background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', color: 'var(--text-secondary)' }}>
                ↓ CSV
              </button>
            </div>
            {subscribers.length === 0 && (
              <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '40px' }}>
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>📭</div>
                <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '4px' }}>Нет подписчиков</div>
                <div style={{ fontSize: '12px' }}>Добавьте блок «newsletter-signup» на страницу</div>
              </div>
            )}
            {subscribers.map(s => (
              <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', background: 'var(--bg-2)', borderRadius: '8px', marginBottom: '6px' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'var(--accent)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: 700, flexShrink: 0 }}>
                  {(s.name ?? s.email)[0].toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.email}</div>
                  {s.name && <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{s.name}</div>}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', flexShrink: 0 }}>{fmtDate(s.createdAt)}</div>
                <button onClick={() => handleDeleteSub(s.id)}
                  style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '13px', flexShrink: 0 }}>×</button>
              </div>
            ))}
          </div>
        ) : (
          /* History tab */
          <div style={{ padding: '20px 24px' }}>
            <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '16px' }}>История отправленных рассылок</div>
            {campaigns.length === 0 && (
              <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '40px' }}>
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>📋</div>
                <div>Рассылок ещё не было</div>
              </div>
            )}
            {campaigns.map(c => (
              <div key={c.id} style={{ background: 'var(--bg-2)', borderRadius: '10px', padding: '14px 16px', marginBottom: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.subject}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                      {fmtDate(c.sentAt)} · {c.recipientCount} получателей
                    </div>
                  </div>
                  <span style={{ fontSize: '11px', fontWeight: 600, padding: '3px 8px', borderRadius: '100px', background: '#d1fae5', color: '#065f46', flexShrink: 0 }}>
                    {c.status === 'SENT' ? '✓ Отправлено' : c.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
